import classNames from 'classnames';
import {
  type AnchorHTMLAttributes,
  type ButtonHTMLAttributes,
  type ComponentType,
  forwardRef,
  type ReactElement,
} from 'react';

import { type ChipTheme } from './chipTheme';

import styles from './chip.module.scss';

export type ChipProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  dataTestId?: string;
  icon?: ReactElement;
  isExternal?: boolean;
  isRounded?: boolean;
  isSmall?: boolean;
  label: string | undefined;
  LinkComponent?: ComponentType<AnchorHTMLAttributes<HTMLAnchorElement> & { scroll?: boolean }>;
  onClick?: () => void;
  scroll?: boolean;
  target?: string;
  theme?: ChipTheme;
  to?: string;
};

export const Chip = forwardRef<HTMLButtonElement, ChipProps>(
  (
    {
      'aria-label': ariaLabel,
      className,
      dataTestId,
      icon,
      isExternal = false,
      isRounded = false,
      isSmall = false,
      label,
      LinkComponent,
      onClick,
      scroll,
      target,
      theme,
      to,
      type = 'button',
      ...props
    },
    ref
  ) => {
    const PARENT_CLASSNAME = 'chip';

    const chipClasses = classNames([
      styles[PARENT_CLASSNAME],
      [styles[`${PARENT_CLASSNAME}--interactive`]],
      {
        [styles[`${PARENT_CLASSNAME}--is-rounded`]]: isRounded,
        [styles[`${PARENT_CLASSNAME}--not-clickable`]]: !to && !type && !onClick,
        [styles[`${PARENT_CLASSNAME}--small`]]: isSmall,
        [styles[`${PARENT_CLASSNAME}--theme-${theme}`]]: theme,
      },
      className,
    ]);

    const content = (
      <>
        {icon && <div className={styles[`${PARENT_CLASSNAME}__icon-container`]}>{icon}</div>}
        {label}
      </>
    );

    const sharedProps = {
      ...props,
      'aria-label': ariaLabel || label,
      className: chipClasses,
      'data-testid': dataTestId,
      onClick,
    };
    const buttonProps = {
      ...sharedProps,
      type,
    };
    const linkProps = {
      ...(sharedProps as AnchorHTMLAttributes<HTMLAnchorElement>),
      href: to,
      target,
    };

    return isExternal ? (
      <a {...linkProps}>{content}</a>
    ) : to && LinkComponent !== undefined ? (
      <LinkComponent {...linkProps} scroll={scroll}>
        {content}
      </LinkComponent>
    ) : (
      <button {...buttonProps} ref={ref}>
        {content}
      </button>
    );
  }
);
